import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  buildFasesEndpoint,
  extractFasesFromReport,
  getPhaseTypeProps,
  sortFases,
} from "./categorias.utils";
import FaseDetalle from "./brackets/FaseDetalle";

const VISTA_LLAVES     = "llaves";
const VISTA_RESULTADOS = "resultados";

// ─── Colores por tipo de fase ─────────────────────────────────────────────────
const PHASE_COLOR_MAP = {
  primary:   "bg-blue-600 text-white border-blue-600",
  secondary: "bg-gray-500 text-white border-gray-500",
  success:   "bg-green-600 text-white border-green-600",
  warning:   "bg-yellow-500 text-white border-yellow-500",
  error:     "bg-red-600 text-white border-red-600",
  default:   "bg-gray-200 text-gray-700 border-gray-200",
};

const PHASE_COLOR_OUTLINE = {
  primary:   "border-blue-400 text-blue-600",
  secondary: "border-gray-400 text-gray-600",
  success:   "border-green-400 text-green-600",
  warning:   "border-yellow-400 text-yellow-600",
  error:     "border-red-400 text-red-600",
  default:   "border-gray-300 text-gray-500",
};

// ─── Selector de fases ────────────────────────────────────────────────────────
const FaseSelector = ({ fases, selectedId, onChange }) => (
  <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-gray-300">
    {fases.map((fase) => {
      const typeProps  = getPhaseTypeProps(fase.phaseType);
      const isSelected = selectedId === fase.phaseId;
      const colorClass = isSelected
        ? (PHASE_COLOR_MAP[typeProps?.color] ?? PHASE_COLOR_MAP.default)
        : `bg-white border ${PHASE_COLOR_OUTLINE[typeProps?.color] ?? PHASE_COLOR_OUTLINE.default}`;

      return (
        <button
          key={fase.phaseId}
          onClick={() => onChange(fase.phaseId)}
          className={`
            flex-shrink-0 px-3 py-1 rounded-full text-xs border
            transition-all duration-200 cursor-pointer
            ${colorClass}
            ${isSelected ? "font-bold" : "font-normal"}
          `}
        >
          {fase.phaseName}
        </button>
      );
    })}
  </div>
);

// ─── Modal principal ──────────────────────────────────────────────────────────
const CategoriaModal = ({ open, onClose, categoria, idsport }) => {
  const [fases, setFases]                     = useState([]);
  const [loadingFases, setLoadingFases]       = useState(false);
  const [errorFases, setErrorFases]           = useState("");
  const [selectedPhaseId, setSelectedPhaseId] = useState(null);
  const [vistaTab, setVistaTab]               = useState(VISTA_LLAVES);
  const backdropRef                           = useRef(null);

  // Cerrar con Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Bloquear scroll del body cuando el modal está abierto
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Cargar fases cuando se abre el modal
  useEffect(() => {
    if (!open || !categoria) return;

    let isMounted = true;
    setFases([]);
    setSelectedPhaseId(null);
    setVistaTab(VISTA_LLAVES);
    setLoadingFases(true);
    setErrorFases("");

    const load = async () => {
      try {
        const res = await fetch(buildFasesEndpoint(categoria.eventCategoryId));
        if (!res.ok) throw new Error(`Error ${res.status}`);
        const data   = await res.json();
        const sorted = sortFases(extractFasesFromReport(data));
        if (!isMounted) return;
        setFases(sorted);
        if (sorted.length) setSelectedPhaseId(sorted[0].phaseId);
      } catch (err) {
        if (!isMounted) return;
        setErrorFases(err.message || "Error cargando fases");
      } finally {
        if (isMounted) setLoadingFases(false);
      }
    };

    load();
    return () => { isMounted = false; };
  }, [open, categoria]);

  const handlePhaseChange = useCallback((phaseId) => {
    setSelectedPhaseId(phaseId);
    setVistaTab(VISTA_LLAVES);
  }, []);

  const selectedFase   = fases.find((f) => f.phaseId === selectedPhaseId) || null;
  const phaseTypeProps = selectedFase ? getPhaseTypeProps(selectedFase.phaseType) : null;
  const chipColor      = PHASE_COLOR_MAP[phaseTypeProps?.color] ?? PHASE_COLOR_MAP.default;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
      {/* Backdrop */}
      <div
        ref={backdropRef}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel del modal */}
      <div
        className="
          relative z-10 bg-white flex flex-col overflow-hidden
          w-full md:max-w-6xl
          h-full md:h-[92vh]
          rounded-none md:rounded-2xl
          animate-slide-up
        "
      >
        {/* ── Header ── */}
        <div className="px-4 md:px-8 pt-4 md:pt-6 pb-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-xl font-extrabold text-blue-600 leading-tight">
                  {categoria?.categoryName}
                </h2>
                {selectedFase && (
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${chipColor}`}>
                    {phaseTypeProps?.label}
                  </span>
                )}
              </div>
            </div>

            {/* Botón cerrar */}
            <button
              onClick={onClose}
              className="flex-shrink-0 p-1.5 rounded-lg border border-gray-200
                         text-gray-500 hover:text-gray-800 hover:bg-gray-100
                         transition-colors duration-150"
              aria-label="Cerrar modal"
            >
              {/* X icon inline SVG */}
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Selector de fases */}
          {fases.length > 0 && (
            <div className="mt-3">
              <span className="block text-[10px] uppercase tracking-widest text-gray-400 mb-1.5">
                Fases
              </span>
              <FaseSelector
                fases={fases}
                selectedId={selectedPhaseId}
                onChange={handlePhaseChange}
              />
            </div>
          )}
        </div>

        {/* ── Contenido ── */}
        <div className="flex flex-col flex-1 overflow-hidden">

          {/* Estado: cargando */}
          {loadingFases && (
            <div className="flex flex-col items-center justify-center flex-1 gap-3">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-500">Cargando fases...</p>
            </div>
          )}

          {/* Estado: error */}
          {!loadingFases && errorFases && (
            <div className="m-6 flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              
              <span>{errorFases}</span>
            </div>
          )}

          {/* Estado: sin fases */}
          {!loadingFases && !errorFases && !fases.length && (
            <div className="m-6 flex items-start gap-2 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
              
              <span>Esta categoría no tiene fases registradas.</span>
            </div>
          )}

          {/* Estado: fase seleccionada */}
          {!loadingFases && selectedPhaseId && (
            <div className="flex flex-col flex-1 overflow-hidden">
              {/* Tabs */}
              <div className="flex px-4 md:px-8 border-b border-gray-200 flex-shrink-0">
                {[
                  { value: VISTA_LLAVES,     label: "MATCHES",    icon: "🏆" },
                  { value: VISTA_RESULTADOS, label: "Resultados", icon: "📊" },
                ].map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => setVistaTab(tab.value)}
                    className={`
                      flex items-center gap-1.5 px-3 py-3 text-xs font-semibold
                      border-b-2 transition-colors duration-150 -mb-px
                      ${vistaTab === tab.value
                        ? "border-blue-600 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700"}
                    `}
                  >
                    <span>{tab.icon}</span>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Contenido de la tab */}
              <div className="flex-1 overflow-y-auto px-2 md:px-6 py-4">
                <FaseDetalle
                  key={`${selectedPhaseId}-${vistaTab}`}
                  eventCategoryId={categoria.eventCategoryId}
                  phaseId={selectedPhaseId}
                  vista={vistaTab}
                  idsport={idsport}              
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoriaModal;